
/* ============================================================
   HG SERIES UNIVERSE — shared animation engine
   Dependency-free
   ============================================================ */
(() => {
  if (window.__HGSeriesUniverseLoaded) return;
  window.__HGSeriesUniverseLoaded = true;

  const clamp = (n, a = 0, b = 1) => Math.min(b, Math.max(a, n));
  const lerp = (a,b,t) => a + (b-a)*t;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function bootLoader() {
    const loader = document.querySelector('[data-hgs-loader]');
    if (!loader) return;
    const count = loader.querySelector('[data-hgs-count]');
    let p = 0;
    const start = performance.now();
    const duration = 1050;

    const tick = now => {
      p = clamp((now - start) / duration);
      const eased = 1 - Math.pow(1-p, 3);
      if (count) count.textContent = String(Math.round(eased * 100)).padStart(3,'0');
      if (p < 1 && !reduced) requestAnimationFrame(tick);
      else {
        if (count) count.textContent = '100';
        setTimeout(() => loader.classList.add('is-done'), reduced ? 0 : 140);
      }
    };
    requestAnimationFrame(tick);
  }

  function bootCursor() {
    if (reduced || matchMedia('(hover: none)').matches) return;
    let cursor = document.querySelector('.hgs-cursor');
    if (!cursor) {
      cursor = document.createElement('div');
      cursor.className = 'hgs-cursor';
      cursor.innerHTML = '<span>VIEW</span>';
      document.body.appendChild(cursor);
    }
    const label = cursor.querySelector('span');
    let x = innerWidth/2, y = innerHeight/2, tx = x, ty = y;
    addEventListener('mousemove', e => {
      tx = e.clientX; ty = e.clientY;
      cursor.classList.add('is-visible');
    }, {passive:true});
    addEventListener('mouseleave', () => cursor.classList.remove('is-visible'));

    const loop = () => {
      x = lerp(x, tx, .18); y = lerp(y, ty, .18);
      cursor.style.left = x+'px'; cursor.style.top = y+'px';
      requestAnimationFrame(loop);
    }; loop();

    document.addEventListener('mouseover', e => {
      const t = e.target.closest('[data-cursor]');
      label.textContent = t ? (t.dataset.cursor || 'VIEW') : 'VIEW';
    });
  }

  function bootHero(hero) {
    if (reduced) return;
    const title = hero.querySelector('.hgs-hero__title');
    const floats = [...hero.querySelectorAll('.hgs-hero__float')];
    let mx=0, my=0, tx=0, ty=0;

    hero.addEventListener('pointermove', e => {
      const r = hero.getBoundingClientRect();
      tx = (e.clientX-r.left)/r.width - .5;
      ty = (e.clientY-r.top)/r.height - .5;
    });
    hero.addEventListener('pointerleave', () => { tx=0; ty=0; });

    const loop = () => {
      mx = lerp(mx,tx,.07); my=lerp(my,ty,.07);
      if (title) title.style.transform = `translate3d(${mx*14}px, ${my*10}px, 0)`;
      floats.forEach((el,i) => {
        const d = (i+1)*9;
        el.style.transform = `translate3d(${mx*d}px, ${my*d*.75}px, 0) rotate(${mx*(i%2?4:-4)}deg)`;
      });
      requestAnimationFrame(loop);
    }; loop();
  }

  function bootIndex(index) {
    const previews = [...index.querySelectorAll('.hgs-index__preview')];
    index.querySelectorAll('.hgs-index__item').forEach(btn => {
      const id = btn.dataset.target;
      const preview = index.querySelector(`[data-preview="${id}"]`);
      btn.addEventListener('mouseenter', () => {
        previews.forEach(p => p.classList.remove('is-active'));
        preview?.classList.add('is-active');
      });
      btn.addEventListener('mouseleave', () => preview?.classList.remove('is-active'));
      btn.addEventListener('click', () => {
        const target = document.getElementById(id);
        target?.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block:'start'});
      });
    });
  }

  function progressFor(pin) {
    const r = pin.getBoundingClientRect();
    const max = Math.max(1, pin.offsetHeight - innerHeight);
    return clamp(-r.top / max);
  }

  function bootChapter(chapter) {
    const pin = chapter.querySelector('.hgs-chapter__pin');
    if (!pin) return;
    const words = [...chapter.querySelectorAll('.hgs-chapter__word')];
    const product = chapter.querySelector('.hgs-chapter__product');
    const statement = chapter.querySelector('.hgs-chapter__statement');
    const media = chapter.querySelector('.hgs-chapter__media img, .hgs-chapter__media video');
    const motion = chapter.dataset.motion || 'dramatic';

    const render = () => {
      if (reduced) return;
      const p = progressFor(pin);
      const e = 1 - Math.pow(1-p, 3);

      words.forEach((w,i) => {
        let x=0,y=0,rot=0,scale=1;
        if (motion === 'dramatic') {
          x = (i%2?1:-1) * p * innerWidth * .10;
          scale = 1 + p*.06;
        } else if (motion === 'kinetic') {
          x = (i%2?1:-1) * Math.sin(p*Math.PI) * innerWidth*.16;
          rot = (i%2?1:-1) * p * 3;
        } else if (motion === 'minimal') {
          y = (i%2?1:-1) * p * 28;
        } else if (motion === 'vertical') {
          y = (i%2?1:-1) * p * innerHeight*.10;
        } else if (motion === 'diagonal') {
          x = (i%2?1:-1) * p * innerWidth*.12;
          y = (i%2?1:-1) * p * 50;
          rot = (i%2?1:-1)*4*p;
        } else if (motion === 'classic') {
          x = (i%2?1:-1) * p * innerWidth*.06;
        }
        w.style.transform = `translate3d(${x}px,${y}px,0) rotate(${rot}deg) scale(${scale})`;
        w.style.opacity = String(clamp(1 - Math.max(0,p-.86)*6));
      });

      if (product) {
        const enter = clamp((p-.08)/.28);
        const leave = clamp((p-.74)/.20);
        let rot = (p-.5)*18;
        let y = (1-enter)*70 - leave*50;
        let scale = .62 + enter*.48 - leave*.06;

        if (motion === 'kinetic') rot *= 1.6;
        if (motion === 'minimal') rot *= .25;
        if (motion === 'vertical') y += (p-.5)*80;
        if (motion === 'diagonal') rot += -6 + p*12;
        if (motion === 'classic') rot *= .18;

        product.style.opacity = String(clamp(enter*(1-leave*.7)));
        product.style.transform = `translate3d(0,${y}px,0) rotate(${rot}deg) scale(${scale})`;
      }

      if (statement) {
        const s = clamp((p-.48)/.24);
        statement.style.opacity = String(s);
        statement.style.transform = `translate3d(0,${(1-s)*42}px,0)`;
      }
      if (media) {
        media.style.transform = `scale(${1.05 + p*.06}) translate3d(0,${p*25}px,0)`;
      }
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) requestAnimationFrame(() => { render(); ticking=false; });
      ticking = true;
    };
    addEventListener('scroll', onScroll, {passive:true});
    addEventListener('resize', onScroll);
    render();
  }

  function bootDragRail(track) {
    let down=false, sx=0, sl=0, lastX=0, velocity=0, raf=0;
    track.addEventListener('pointerdown', e => {
      down=true; sx=e.clientX; sl=track.scrollLeft; lastX=e.clientX; velocity=0;
      track.setPointerCapture?.(e.pointerId);
      track.classList.add('is-dragging');
    });
    track.addEventListener('pointermove', e => {
      if (!down) return;
      const dx=e.clientX-sx;
      velocity=e.clientX-lastX; lastX=e.clientX;
      track.scrollLeft=sl-dx;
    });
    const end=() => {
      if (!down) return;
      down=false; track.classList.remove('is-dragging');
      cancelAnimationFrame(raf);
      const glide=()=>{
        velocity*=.92;
        track.scrollLeft-=velocity*1.25;
        if(Math.abs(velocity)>.35) raf=requestAnimationFrame(glide);
      };
      raf=requestAnimationFrame(glide);
    };
    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
  }

  function bootTransitions() {
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const slit = entry.target.querySelector('.hgs-transition__slit');
        if (slit && !reduced) slit.animate(
          [{transform:'translateY(-50%) scaleX(.08) scaleY(1)'},
           {transform:'translateY(-50%) scaleX(1) scaleY(1)'},
           {transform:'translateY(-50%) scaleX(1) scaleY(180)'}],
          {duration:1300, easing:'cubic-bezier(.16,1,.3,1)', fill:'forwards'}
        );
        io.unobserve(entry.target);
      });
    }, {threshold:.45});
    document.querySelectorAll('.hgs-transition').forEach(el => io.observe(el));
  }

  function bootArchive(archive) {
    if (reduced) return;
    const wall = archive.querySelector('.hgs-archive__wall');
    if (!wall) return;
    const render=()=>{
      const r=archive.getBoundingClientRect();
      const p=clamp((innerHeight-r.top)/(innerHeight+r.height));
      wall.style.transform=`translate3d(${(p-.5)*8}vw, ${(p-.5)*-8}vw, 0) rotate(-4deg)`;
    };
    addEventListener('scroll', ()=>requestAnimationFrame(render), {passive:true});
    render();
  }

  function init(root=document) {
    bootLoader();
    bootCursor();
    root.querySelectorAll('.hgs-hero').forEach(bootHero);
    root.querySelectorAll('.hgs-index').forEach(bootIndex);
    root.querySelectorAll('.hgs-chapter').forEach(bootChapter);
    root.querySelectorAll('.hgs-rail__track').forEach(bootDragRail);
    root.querySelectorAll('.hgs-archive').forEach(bootArchive);
    bootTransitions();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', e => init(e.target));
})();
